import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";
import { setSeo, setJsonLd } from "../lib/seo";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

function Block({ block }) {
  if (block.type === "h2") return <h2 className="font-heading text-2xl font-bold tracking-tight mt-10 mb-3 text-white">{block.text}</h2>;
  if (block.type === "p") return <p className="text-white/70 leading-relaxed mb-4 text-[17px]">{block.text}</p>;
  if (block.type === "ul")
    return (
      <ul className="my-4 space-y-2">
        {block.items.map((it, i) => (
          <li key={i} className="flex gap-3 text-white/70">
            <span className="text-neon-cyan mt-1">▹</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  if (block.type === "quote")
    return (
      <blockquote className="my-8 border-l-2 border-neon-cyan pl-5 py-1 text-xl font-heading text-neon-cyan/90 italic">
        {block.text}
      </blockquote>
    );
  return null;
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    setPost(null);
    api
      .get(`/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
    api
      .get("/blog")
      .then((res) => setRelated((res.data || []).filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (post) {
      setSeo({
        title: `${post.title} | vonmal Blog`,
        description: post.description,
        image: post.image,
        url: `https://vonmal.com/blog/${post.slug}`,
      });
      setJsonLd("article-schema", {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.description,
        image: post.image,
        datePublished: post.date,
        dateModified: post.date,
        keywords: (post.tags || []).join(", "),
        author: { "@type": "Organization", name: "vonmal", url: "https://vonmal.com/" },
        publisher: {
          "@type": "Organization",
          name: "vonmal",
          logo: { "@type": "ImageObject", url: "https://vonmal.com/vonmal-developer-icon-512.png" },
        },
        mainEntityOfPage: `https://vonmal.com/blog/${post.slug}`,
      });
      setJsonLd("breadcrumb-schema", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://vonmal.com/" },
          { "@type": "ListItem", position: 2, name: "Blog", item: "https://vonmal.com/blog" },
          { "@type": "ListItem", position: 3, name: post.title, item: `https://vonmal.com/blog/${post.slug}` },
        ],
      });
    }
  }, [post]);

  if (notFound) {
    return (
      <div className="bg-void min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-40 text-center">
          <h1 className="font-heading text-3xl font-black">Post not found</h1>
          <button onClick={() => navigate("/blog")} className="mt-6 text-neon-cyan hover:underline">← Back to blog</button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-void min-h-screen">
        <Navbar />
        <div data-testid="blogpost-loading" className="max-w-3xl mx-auto px-5 py-40 text-center font-mono text-sm text-neon-cyan/70 animate-pulse">
          // loading article...
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-void min-h-screen">
      <Navbar />
      <article className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link to="/blog" data-testid="blogpost-back" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-neon-cyan transition-colors mb-8">
            <ArrowLeft size={16} /> All articles
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex flex-wrap items-center gap-3 text-xs font-mono text-white/45">
              <span>{fmt(post.date)}</span>
              <span className="flex items-center gap-1"><Clock size={12} /> {post.read_time}</span>
              {(post.tags || []).map((t) => (
                <span key={t} className="text-neon-cyan border border-neon-cyan/20 rounded-full px-2.5 py-0.5">{t}</span>
              ))}
            </div>
            <h1 data-testid="blogpost-title" className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-tight">
              {post.title}
            </h1>
          </motion.div>

          <div className="mt-8 rounded-2xl overflow-hidden border border-neon-cyan/15">
            <img src={post.image} alt={post.title} loading="lazy" className="w-full h-[280px] sm:h-[380px] object-cover" />
          </div>

          <div className="mt-10">
            {post.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 rounded-2xl border border-neon-cyan/20 bg-surface p-8 text-center">
            <h3 className="font-heading text-2xl font-black tracking-tight">Ready to build your AI app?</h3>
            <p className="mt-2 text-white/55">Get a live price &amp; timeline in under a minute.</p>
            <Link to="/" state={{ scrollTo: "estimate" }} data-testid="blogpost-cta" className="neon-btn inline-block mt-6 bg-neon-cyan text-black font-semibold px-7 py-3 rounded-full">
              Build your app
            </Link>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
            <h3 className="font-heading text-xl font-bold mb-6">More articles</h3>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block glow-border rounded-2xl border border-neon-cyan/15 bg-surface overflow-hidden">
                  <div className="relative h-36 overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
                  </div>
                  <div className="p-5">
                    <h4 className="font-bold tracking-tight group-hover:text-neon-cyan transition-colors line-clamp-2">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
}
